import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { offerAPI } from '../../services/api';
import { Inbox } from 'lucide-react';
import { DashboardSkeleton } from '../../components/skeletons';
import { AttributeValues } from '../../components/AttributeFields';

function formatOfferDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getDate();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

const S = {
  page: { padding: '24px 20px', background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 20 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 },
  stat: { padding: '18px 16px', borderRadius: 12, background: '#fff', border: '1.5px solid var(--border)', textAlign: 'center', boxShadow: 'var(--shadow-sm)', transition: 'transform 0.2s ease, box-shadow 0.2s ease' },
  statLabel: { fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4, fontWeight: 700 },
  statVal: { fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 700 },
  card: { padding: 20, borderRadius: 14, background: '#fff', border: '1.5px solid var(--border)', marginBottom: 14, boxShadow: 'var(--shadow-sm)', transition: 'all 0.2s ease' },
  tag: { display: 'inline-block', padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, marginRight: 6 },
  statusBadge: (status) => ({
    display: 'inline-block', padding: '5px 14px', borderRadius: 14, fontSize: 11, fontWeight: 750, textTransform: 'uppercase', letterSpacing: 0.5,
    background: status === 'accepted' ? '#E8F5EE' : status === 'rejected' ? '#FEE2E2' : '#FFF8E7',
    color: status === 'accepted' ? '#1B5C3A' : status === 'rejected' ? '#DC2626' : '#F5A623',
  }),
  scoreBadge: (score) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 24, height: 24, borderRadius: '50%', fontSize: 10, fontWeight: 700, color: '#fff',
    background: score > 4 ? '#1B5C3A' : score > 3 ? '#F5A623' : '#DC2626',
  }),
  emptyBox: { textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 14, border: '1.5px solid var(--border)' },
};

function timeAgo(date) {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function MyOffers() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    offerAPI.mine()
      .then(r => setOffers(r.data.data || []))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  const total = offers.length;
  const accepted = offers.filter(o => o.status === 'accepted').length;
  const pending = offers.filter(o => o.status === 'pending').length;
  const rejected = offers.filter(o => o.status === 'rejected').length;
  const winRate = total ? Math.round((accepted / total) * 100) : 0;

  const statsData = [
    { label: 'Total Offers', value: total, color: 'var(--char)' },
    { label: 'Accepted', value: accepted, color: 'var(--green)' },
    { label: 'Pending', value: pending, color: 'var(--gold)' },
    { label: 'Rejected', value: rejected, color: '#DC2626' },
    { label: 'Win Rate', value: `${winRate}%`, color: winRate >= 50 ? 'var(--green)' : 'var(--gold)' },
  ];

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div style={S.page}>
      <div style={S.title}>My Offers</div>
      <div style={S.subtitle}>Track all your submitted offers and their status</div>

      {/* Stats */}
      <div style={S.statsRow}>
        {statsData.map((s, i) => (
          <div key={i} style={S.stat}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
            }}
          >
            <div style={S.statLabel}>{s.label}</div>
            <div style={{ ...S.statVal, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Offer Cards */}
      {offers.length === 0 ? (
        <div style={S.emptyBox}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <Inbox size={40} color="var(--muted)" style={{ opacity: 0.8 }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No offers yet</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Browse demands and send your first offer</div>
        </div>
      ) : (
        offers.map(o => {
          const demand = o.demandId || {};
          const cat = demand.categoryId?.name || '';
          const sc = demand.subCategoryId?.name || '';
          const brand = demand.brandId?.name || '';
          const buyerPrice = demand.price;
          const offeredPrice = o.price;
          const offerDate = formatOfferDate(o.createdAt);
          return (
            <div key={o._id} className="card" style={S.card}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--green)';
                e.currentTarget.style.boxShadow = 'var(--shadow)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--char)' }}>
                      {demand.title || 'Demand'}
                    </span>
                    {cat && <span style={{ ...S.tag, background: 'var(--green-lt, #E8F5EE)', color: 'var(--green)', marginRight: 0 }}>{cat}</span>}
                    {sc && <span style={{ ...S.tag, background: '#f3f4f6', color: '#4b5563', marginRight: 0 }}>{sc}</span>}
                    {brand && <span style={{ ...S.tag, background: '#eff6ff', color: '#1d4ed8', marginRight: 0 }}>{brand}</span>}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Buyer Price: <strong style={{ color: 'var(--char)', fontSize: 13 }}>₹{buyerPrice?.toLocaleString('en-IN') || '--'}</strong>
                    </div>
                    <div style={{ height: 12, width: 1, background: 'var(--border)' }} />
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Your Offered Price: <strong style={{ color: 'var(--green)', fontSize: 14, fontFamily: 'var(--font-serif)', fontWeight: 700 }}>₹{offeredPrice?.toLocaleString('en-IN') || 0}</strong>
                    </div>
                  </div>

                  {Array.isArray(demand.attributes) && demand.attributes.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <AttributeValues items={demand.attributes} compact />
                    </div>
                  )}

                  <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginTop: 10 }}>
                    Offered on: <strong style={{ color: 'var(--char)' }}>{offerDate}</strong>
                  </div>

                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <span style={S.statusBadge(o.status)}>{o.status}</span>
                  <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>{timeAgo(o.createdAt)}</span>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
